import { Account } from '../../../types'; // Adjust the import path as necessary
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Picker from '../../widgets/components/SimplePicker';
import { fetchAccount, upsertAccount } from '../../../api';
import { generateShortUUID } from '../../misc_objects/Utils';
import { accountTypeCategoryMap } from '../../../DS-Files';
import { AccountCategory, AccountType } from '../../../Constants';
import { toast } from 'react-toastify';
import SliderSwitch from '../../widgets/components/SliderSwitch';
import DatePicker from '../../widgets/components/DatePicker';
import { scrollToFirstError } from '../../utils/MiscFunctions';


const accountTypeOptions = Object.values(AccountType).map((type) => ({
  value: type.toString(),
  label: type.toString(),
}));

function getAccountCategoryOptionsByType(selectedAccountType?: AccountType) {
  if (!selectedAccountType) {
    // Return an empty array or provide default options if AccountType is undefined
    return Object.values(AccountCategory).map((type) => ({
      value: type.toString(),
      label: type.toString(),
    }));
  }
  const validCategories = accountTypeCategoryMap[selectedAccountType] || [];
  return validCategories.map((category) => ({
    value: category.toString(),
    label: category.toString(),
  }));
}

interface AccountFormProps {
  editAccount?: string;
  onSave?: (data: Account) => void;
  accountTypes?: AccountType[]
  accountCategories?: AccountCategory[]
  onClose: () => void;
}

const AccountForm: React.FC<AccountFormProps> = ({ editAccount, onSave, accountTypes, accountCategories, onClose }) => {
  const { register, handleSubmit, control, reset, watch, formState: { errors } } = useForm<Account>();
  const [account, setAccount] = useState<Account>();
  const [accountCategoryOptions, setAccountCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [addOpeningBalance, setAddOpeningBalance] = useState<boolean>(false);
  // Watch the form for changes to accountType
  const watchedAccountType = watch('accountType', account?.accountType);

  useEffect(() => {
    // Update category options whenever the account type changes
    setAccountCategoryOptions(getAccountCategoryOptionsByType(watchedAccountType));
  }, [watchedAccountType]);

  useEffect(() => {
    const getAccount = async () => {
      if (editAccount) {
        const accountResponse = await fetchAccount(editAccount);
        if (accountResponse) {
          setAccount(accountResponse);
          setAddOpeningBalance(Boolean(accountResponse.openingBalanceAmt))
          reset(accountResponse); // Reset the form with the fetched account
        }
      } else {
        // MBAI-520: when the picker that opened this form is scoped to a single
        // account type/category (e.g. the Expense Category picker), pre-select it
        // instead of making the user pick it again from the full list.
        const presetAccountType = accountTypes && accountTypes.length === 1 ? accountTypes[0] : undefined;
        const presetAccountCategory = accountCategories && accountCategories.length === 1 ? accountCategories[0] : undefined;
        const newAccount = new Account({
          accountId: generateShortUUID(),
          ...(presetAccountType ? { accountType: presetAccountType } : {}),
          ...(presetAccountCategory ? { accountCategory: presetAccountCategory } : {}),
        });
        setAccount(newAccount);
        reset(newAccount); // Reset form with new account data
      }
    };
    getAccount();
  }, [editAccount, reset]);

  const handleSave = async (data: Account) => {
    try {
      const updateAccount = await upsertAccount(data);
      toast.success('Account saved successfully!'.toTranslatedDisplay());
    } catch (error) {
      if (error instanceof Error) {
        toast.error('Error Saving Account: '.toTranslatedDisplay() + error.message);
      } else {
        toast.error('An unknown error occurred.'.toTranslatedDisplay());
      }
    }
  };

  const onSubmit = async (data: Account) => {
    await handleSave(data)
    if (onSave) onSave(data);
  };

  // MBAI-28: scroll to first invalid field on validation failure
  const onInvalid = (formErrors: typeof errors) => {
    const firstErrorField = Object.keys(formErrors)[0];
    if (firstErrorField) scrollToFirstError(firstErrorField);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="p-3">
      <div className="mb-3">
        <label className="form-label">{'Account Name'.toTranslatedDisplay()}</label>
        <input
          className={`form-control ${errors.accountName ? 'is-invalid' : ''}`}
          {...register('accountName', { required: 'Account Name is required' })}
          defaultValue={account?.accountName || ''}
          placeholder={'Account Name'.toTranslatedDisplay()}
        />
        {errors.accountName && <div className="invalid-feedback">{errors.accountName.message}</div>}
      </div>

      <div className="mb-3">
        <Controller
          name="accountType"
          control={control}
          rules={{ required: 'Account Type is required' }}
          render={({ field }) => (
            <Picker
              label={'Account Type'.toTranslatedDisplay()}
              name="accountType"
              disabled={Boolean(editAccount)}
              value={field.value}
              options={accountTypeOptions.filter(option =>
                accountTypes ? accountTypes.includes(option.value as AccountType) : true
              )}
              placeholder={'Select Account Type'.toTranslatedDisplay()}
              error={errors.accountType?.message}
              onSelectionChange={(e) => {
                field.onChange(e.value);
              }}
            />
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="accountCategory"
          control={control}
          render={({ field }) => (
            <Picker
              label={'Account Category'.toTranslatedDisplay()}
              disabled={Boolean(editAccount)}
              value={field.value}
              options={accountCategoryOptions.filter(option =>
                accountCategories ? accountCategories.includes(option.value as AccountCategory) : true
              )}
              placeholder={'Select Account Category'.toTranslatedDisplay()}
              onSelectionChange={(e) => {
                field.onChange(e.value);
              }}
            />
          )}
        />
      </div>



      <div className="mb-3">
        <label className="form-label">{'Account Description'.toTranslatedDisplay()}</label>
        <input className="form-control" {...register('accountDesc')} placeholder={'Account Description (Optional)'.toTranslatedDisplay()} />
      </div>
      {!addOpeningBalance ?
        <div className='d-flex flex-row mt-1 mb-3'>
          <SliderSwitch name='openingBalance' defaultChecked={addOpeningBalance} onChange={() => {
            setAddOpeningBalance(true)
          }} />
          <p className='mb-0 ms-2'>{'Add Opening Balance?'.toTranslatedDisplay()}</p>
        </div> 
        :
        <div className='d-flex flex-row mt-1 mb-3'>
          <div className='w-50'>
            <label className="form-label">{'Opening Balance Amount'.toTranslatedDisplay()}</label>
            <input className="form-control" {...register('openingBalanceAmt')} placeholder={'Opening Balance'.toTranslatedDisplay()} />
          </div>
          <div className='ms-3 w-50'>
            <label className="form-label">{'Opening Balance Date'.toTranslatedDisplay()}</label>
            <Controller
          name="openingBalanceDate"
          control={control}
          render={({ field }) => (
            <DatePicker value={field.value} onChange={function (date: Date): void {
              field.onChange(date);
            } } />
          )}
        />
          </div>
        </div>
      }

      <div className="mb-3">
        <label className="form-label">{'Account Code'.toTranslatedDisplay()}</label>
        
        <input className="form-control" {...register('accountCode')} placeholder={'Optional'.toTranslatedDisplay()} />
      </div>



      <div className="form-check mb-3">
        <input className="form-check-input" type="checkbox" {...register('subAccountFlg')} />
        <label className="form-check-label">{'Check here for choosing this as sub account'.toTranslatedDisplay()}</label>
      </div>

      <div className="d-flex justify-content-start">
        <button type="submit" className="btn btn-primary">{'Save'.toTranslatedDisplay()}</button>
        <button type="button" onClick={onClose} className="ms-2 btn btn-secondary me-2">{'Close'.toTranslatedDisplay()}</button>
      </div>
    </form>
  );
};

export default AccountForm;
